import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { QuickLook } from "@eventr/native/quicklook";
import { spErrorTags } from "@eventr/common";
import { CacheFile, getRandomString } from "utils";
import { useImageFrameStore } from "./MobileImageFrame";

const PREVIEW_DIR = "quicklook-preview";

const EXTENSION_BY_MIME_TYPE: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/heic": "heic",
  "image/gif": "gif",
  "image/webp": "webp",
  "application/pdf": "pdf",
};

export function isIosQuickLookAvailable(): boolean {
  return Capacitor.isPluginAvailable("QuickLook");
}

function fallbackToLightbox(source: string): void {
  useImageFrameStore.getState().openImageFrame(source);
}

/** Opens `source` (a `data:` URI or a remote URL) with native Quick Look on iOS, or the JS lightbox otherwise. */
export async function openImagePreview(source: string, filename?: string): Promise<void> {
  if (!isIosQuickLookAvailable()) {
    fallbackToLightbox(source);
    return;
  }

  try {
    if (source.startsWith("data:")) {
      await previewDataUri(source, filename);
    } else {
      await previewRemoteUrl(source);
    }
  } catch (error) {
    spErrorTags("openImagePreview", ["FILES"], error);
    fallbackToLightbox(source);
  }
}

/** Click handler for a tappable avatar, or `undefined` if there's nothing to preview. */
export function previewOnClick(url: string | undefined, enabled: boolean): (() => void) | undefined {
  if (!enabled || !url) return undefined;
  return () => void openImagePreview(url);
}

async function previewDataUri(dataUri: string, filename?: string): Promise<void> {
  const commaIndex = dataUri.indexOf(",");
  const meta = dataUri.slice(5, commaIndex); // strips leading "data:"
  const base64 = dataUri.slice(commaIndex + 1);
  const mimeType = meta.split(";")[0] ?? "";
  const filenameExtension = filename?.includes(".") ? filename.split(".").pop() : undefined;
  const extension = filenameExtension || EXTENSION_BY_MIME_TYPE[mimeType] || "bin";

  await ensurePreviewDir();
  const path = `${PREVIEW_DIR}/${getRandomString(16)}.${extension}`;
  await Filesystem.writeFile({ path, data: base64, directory: Directory.Cache });

  try {
    const { uri } = await Filesystem.getUri({ path, directory: Directory.Cache });
    await QuickLook.preview({ path: uri });
  } finally {
    // Scratch copy for Quick Look - must not accumulate on device.
    await Filesystem.deleteFile({ path, directory: Directory.Cache }).catch(() => {});
  }
}

async function previewRemoteUrl(url: string): Promise<void> {
  const { path } = await CacheFile(url, true);
  const { uri } = await Filesystem.getUri({ path, directory: Directory.Cache });
  // Not deleted - reuses the app's persistent download cache, shared with other consumers.
  await QuickLook.preview({ path: uri });
}

async function ensurePreviewDir(): Promise<void> {
  try {
    await Filesystem.mkdir({ path: PREVIEW_DIR, directory: Directory.Cache, recursive: true });
  } catch {
    // already exists
  }
}

/** Clears any preview temp file left behind by an app kill mid-preview. Call once at startup. */
export async function sweepStaleImagePreviews(): Promise<void> {
  try {
    const { files } = await Filesystem.readdir({ path: PREVIEW_DIR, directory: Directory.Cache });
    await Promise.all(
      files.map((file) =>
        Filesystem.deleteFile({ path: `${PREVIEW_DIR}/${file.name}`, directory: Directory.Cache }).catch(() => {}),
      ),
    );
  } catch {
    // directory doesn't exist yet - nothing to sweep
  }
}
